import React from 'react';
import { Device, DeviceStatus } from '../../types/device';
import { CheckCircle, AlertCircle, Cog } from 'lucide-react';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';

// Status badge colors
const statusBadgeColors: Record<any, string> = {
  Pending: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
  Failed: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
  CertificateAvailable: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
  DownloadComplete: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
  Deregistered: 'bg-page-bg text-heritage-grey border border-[#E2E5EA]',
  AdminApprovalPending: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
  AdminApproved: "text-galvanizing-green bg-[rgba(31,154,58,0.08)]",
  AdminRejected: 'bg-page-bg text-heritage-grey border border-[#E2E5EA]',
  Register: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
  PublishReady: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
  AdminApproval: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
};

// Status names mapping
const statusName: Record<any, string> = {
  Pending: 'Registration Pending',
  Failed: 'Registration Failed',
  CertificateAvailable: 'Certificate Available',
  DownloadComplete: 'Certificate Downloaded',
  Deregistered: 'Decommissioned',
  AdminApprovalPending: 'Admin Approval Pending',
  AdminApproved: 'Admin Approved',
  AdminRejected: 'Admin Rejected',
  Register: 'Registered',
  PublishReady: 'Active',
  AdminApproval: 'Admin Approval',
};

// User status map
const userStatusMap: Record<
  string,
  { label: string; color: string; backends: string[]; icon: any; description: string }
> = {
  'Registration Pending': {
    label: 'Registration Pending',
    color: 'text-readable-red',
    backends: ['Pending'],
    icon: AlertCircle,
    description: 'Device registration is pending'
  },
  'Registration Failed': {
    label: 'Registration Failed',
    color: 'text-readable-red',
    backends: ['Failed'],
    icon: AlertCircle,
    description: 'Device registration has failed'
  },
  'Certificate Available': {
    label: 'Certificate Available',
    color: 'text-[#B38600]',
    backends: ['CertificateAvailable'],
    icon: AlertCircle,
    description: 'Certificate is available for download'
  },
  'Download Complete': {
    label: 'Download Complete',
    color: 'text-galvanizing-green',
    backends: ['DownloadComplete'],
    icon: CheckCircle,
    description: 'Download has been completed'
  },
  'Decommissioned': {
    label: 'Decommissioned',
    color: 'text-heritage-grey',
    backends: ['Deregistered'],
    icon: AlertCircle,
    description: 'Device has been decommissioned'
  },
  'Admin Approval Pending': {
    label: 'Admin Approval Pending',
    color: 'text-[#B38600]',
    backends: ['AdminApprovalPending', 'AdminApproval'],
    icon: AlertCircle,
    description: 'Waiting for admin approval'
  },
  'Admin Approved': {
    label: 'Admin Approved',
    color: 'text-galvanizing-green',
    backends: ['AdminApproved'],
    icon: CheckCircle,
    description: 'Device has been approved by admin'
  },
  'Admin Rejected': {
    label: 'Admin Rejected',
    color: 'text-heritage-grey',
    backends: ['AdminRejected'],
    icon: AlertCircle,
    description: 'Device has been rejected by admin'
  },
  'Registered': {
    label: 'Registered',
    color: 'text-[#B38600]',
    backends: ['Register'],
    icon: CheckCircle,
    description: 'Device is registered'
  },
  'Publish Ready': {
    label: 'Publish Ready',
    color: 'text-galvanizing-green',
    backends: ['PublishReady'],
    icon: CheckCircle,
    description: 'Device is ready to publish'
  },
};

interface StatusUpdateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDevice: Device | null;
  selectedStatusForUpdate: DeviceStatus | null;
  setSelectedStatusForUpdate: (status: DeviceStatus | null) => void;
  onStatusUpdate: () => void;
  isUpdatingStatus: boolean;
  availableStatuses: string[];
}

export const StatusUpdateDialog: React.FC<StatusUpdateDialogProps> = ({
  isOpen,
  onClose,
  selectedDevice,
  selectedStatusForUpdate,
  setSelectedStatusForUpdate,
  onStatusUpdate,
  isUpdatingStatus,
  availableStatuses,
}) => {
  const getStatusBadge = (status: DeviceStatus) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${statusBadgeColors[status]}`}>
      {statusName[status] || status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[500px] p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-[rgba(0,39,63,0.06)] rounded-full flex items-center justify-center">
            <Cog className="h-5 w-5 text-blueprint-blue" />
          </div>
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
            Update Device Status
          </DialogTitle>
        </div>

        {selectedDevice && (
          <div className="space-y-4">
            <div className="p-3 bg-page-bg border border-[#E2E5EA] rounded-md space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-heritage-grey">Device ID</span>
                <span className="font-mono text-[12px] font-medium text-blueprint-blue">{selectedDevice.id}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-heritage-grey">Assigned Project</span>
                <span className="text-[12px] font-medium text-text-grey">{selectedDevice.project?.name || 'No project assigned'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-heritage-grey">Current Status</span>
                <div>{selectedDevice.status === 'AdminApproval' ? getStatusBadge('AdminApprovalPending') : getStatusBadge(selectedDevice.status)}</div>
              </div>
            </div>

            {/* PublishReady - Deactivate confirmation */}
            {(selectedDevice.status === 'PublishReady') && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={selectedStatusForUpdate === 'Deregistered'}
                    onCheckedChange={(checked) => {
                      setSelectedStatusForUpdate(checked ? 'Deregistered' : null);
                    }}
                    id="deactivate-device"
                  />
                  <label htmlFor="deactivate-device" className="text-[13px] font-medium text-text-grey">
                    Yes, decommission this device
                  </label>
                </div>
                <div className="flex items-start gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-md">
                  <AlertCircle className="h-4 w-4 text-hero-red flex-shrink-0 mt-0.5" />
                  <div className="text-[13px] text-hero-red">
                    <strong className="font-semibold">Decommission Device?</strong><br />
                    Decommissioning this device will remove it from active use. An administrator may reactivate it later.
                  </div>
                </div>
              </div>
            )}

            {/* Deregistered - Approve confirmation */}
            {(selectedDevice.status === 'Deregistered') && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={selectedStatusForUpdate === 'Register'}
                    onCheckedChange={(checked) => {
                      setSelectedStatusForUpdate(checked ? 'Register' : null);
                    }}
                    id="approve-device"
                  />
                  <label htmlFor="approve-device" className="text-[13px] font-medium text-text-grey">
                    Yes, activate this device
                  </label>
                </div>
                <div className="flex items-start gap-2 p-3 bg-[rgba(0,39,63,0.06)] border border-[#E2E5EA] rounded-md">
                  <CheckCircle className="h-4 w-4 text-blueprint-blue flex-shrink-0 mt-0.5" />
                  <div className="text-[13px] text-blueprint-blue">
                    <strong className="font-semibold">Approve Device?</strong><br />
                    {selectedDevice.project?.name
                      ? "Activates the device for project assignment."
                      : "Activates the device. A project is already assigned."}
                  </div>
                </div>
              </div>
            )}

            {/* AdminApproval/AdminRejected - Approve/Reject confirmation */}
            {(selectedDevice.status === 'AdminApproval' || selectedDevice.status === 'AdminRejected') && (
              <div className="space-y-3">
                <label className="block text-[12px] font-medium text-heritage-grey">
                  Select New Status
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedStatusForUpdate === 'AdminApproved'}
                      onCheckedChange={(checked) => {
                        setSelectedStatusForUpdate(checked ? 'AdminApproved' : null);
                      }}
                      id="approve-device"
                    />
                    <label htmlFor="approve-device" className="text-[13px] font-medium text-text-grey">
                      Approve
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedStatusForUpdate === 'AdminRejected'}
                      onCheckedChange={(checked) => {
                        setSelectedStatusForUpdate(checked ? 'AdminRejected' : null);
                      }}
                      id="reject-device"
                    />
                    <label htmlFor="reject-device" className="text-[13px] font-medium text-text-grey">
                      Reject
                    </label>
                  </div>
                </div>
                {selectedStatusForUpdate && (
                  <>
                    {selectedStatusForUpdate === 'AdminApproved' ? (
                      <div className="flex items-start gap-2 p-3 bg-[rgba(0,39,63,0.06)] border border-[#E2E5EA] rounded-md">
                        <CheckCircle className="h-4 w-4 text-blueprint-blue flex-shrink-0 mt-0.5" />
                        <div className="text-[13px] text-blueprint-blue">
                          <strong className="font-semibold">Approve device?</strong><br />
                          This will approve the device and it moves to active or ready to assign to a project.
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-md">
                        <AlertCircle className="h-4 w-4 text-hero-red flex-shrink-0 mt-0.5" />
                        <div className="text-[13px] text-hero-red">
                          <strong className="font-semibold">Reject device?</strong><br />
                          This device will be rejected now; admin can approve it later.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
             {/* Other statuses - Show registration message */}
             {(
              selectedDevice.status === 'DownloadComplete') && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 bg-[rgba(230,161,0,0.08)] border border-[rgba(230,161,0,0.2)] rounded-md">
                    <AlertCircle className="h-4 w-4 text-[#B38600] flex-shrink-0 mt-0.5" />
                    <div className="text-[13px] text-[#B38600]">
                      <strong className="font-semibold">Certificate Downloaded?</strong><br />
                      Certificate has been downloaded for the device. The device is waiting to be assigned to a project.
                    </div>
                  </div>
                </div>
              )}

            {/* Other statuses - Show registration message */}
            {(selectedDevice.status === 'Register' || selectedDevice.status === 'CertificateAvailable' ||
              selectedDevice.status === 'Pending') && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 bg-[rgba(230,161,0,0.08)] border border-[rgba(230,161,0,0.2)] rounded-md">
                    <AlertCircle className="h-4 w-4 text-[#B38600] flex-shrink-0 mt-0.5" />
                    <div className="text-[13px] text-[#B38600]">
                      <strong className="font-semibold">Enable Registration?</strong><br />
                      Re-register initiated for the device. The device is waiting for admin approval.
                    </div>
                  </div>
                </div>
              )}

            {/* Other statuses - Show status options */}
            {(selectedDevice.status === 'PublishReady11' || selectedDevice.status === 'DownloadComplete11') && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select New Status
                </label>
                <div className="space-y-2">
                  {availableStatuses.filter(status => status !== selectedDevice.status).map((status) => {
                    const statusConfig = Object.values(userStatusMap).find(config =>
                      config.backends.includes(status as DeviceStatus)
                    );
                    const Icon = statusConfig?.icon || AlertCircle;
                    const color = statusConfig?.color || "text-gray-500";

                    return (
                      <label key={status} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <Checkbox
                          checked={selectedStatusForUpdate === status}
                          onCheckedChange={(checked) => {
                            setSelectedStatusForUpdate(checked ? status as DeviceStatus : null);
                          }}
                          id={`status-${status}`}
                        />
                        <Icon className={`h-4 w-4 ${color}`} />
                        <span className="text-sm">{statusName[status] || status}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => {
              onClose();
              setSelectedStatusForUpdate(null);
            }}
            className="min-w-[100px] border-[#E2E5EA] text-text-grey hover:bg-page-bg"
          >
            Cancel
          </Button>

          <Button
            onClick={onStatusUpdate}
            disabled={!selectedStatusForUpdate || isUpdatingStatus}
            className="min-w-[100px] bg-hero-red hover:bg-hero-red-60 text-white"
            loading={isUpdatingStatus}
            loadingText="Updating..."
          >
            Update Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
